import { ChangeDetectionStrategy, Component, Directive, ViewEncapsulation, computed, contentChild, input, output } from '@angular/core';

export type CardVariant = 'elevated' | 'interactive';

@Directive({
  selector: '[zagCardHeaderEnd]',
})
export class CardHeaderEnd {}

@Component({
  selector: 'zag-card',
  templateUrl: './card.html',
  styleUrls: ['./card.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.zag-card]': 'true',
    '[class.zag-card--elevated]': 'variant() === "elevated"',
    '[class.zag-card--interactive]': 'variant() === "interactive"',
    '[attr.tabindex]': 'variant() === "interactive" ? 0 : null',
    '[attr.role]': 'variant() === "interactive" ? "button" : "article"',
    '(click)': 'onCardClick($event)',
    '(keydown.enter)': 'onCardKeydown($event)',
    '(keydown.space)': 'onCardKeydown($event)',
    '(keyup.space)': 'onCardKeyup($event)',
  },
})
export class Card {
  /** Card variant: elevated (static) or interactive (clickable) */
  readonly variant = input<CardVariant>('elevated');

  /** Uppercase caption heading; enables the `[zagCardHeaderEnd]` slot (info popover, segmented control, status) beside it */
  readonly label = input<string>('');

  readonly labelLevel = input<1 | 2 | 3 | 4 | 5 | 6>(3);

  readonly hasLabel = computed(() => (this.label() ?? '').trim() !== '');
  readonly headerEnd = contentChild(CardHeaderEnd);
  readonly hasLabelRow = computed(() => this.hasLabel() || this.headerEnd() !== undefined);

  /** Emitted when interactive card is clicked or activated via keyboard */
  readonly cardClick = output<void>();

  onCardClick(event: MouseEvent): void {
    if (!this.isInteractive() || event.defaultPrevented || this.isFromNestedControl(event)) {
      return;
    }

    this.cardClick.emit();
  }

  onCardKeydown(event: Event): void {
    if (!this.isInteractive() || this.isFromNestedControl(event)) {
      return;
    }

    if ((event as KeyboardEvent).key === ' ') {
      event.preventDefault();
    }

    if ((event as KeyboardEvent).key === 'Enter') {
      this.cardClick.emit();
    }
  }

  onCardKeyup(event: Event): void {
    if (this.isInteractive() && !this.isFromNestedControl(event) && (event as KeyboardEvent).key === ' ') {
      this.cardClick.emit();
    }
  }

  private isInteractive(): boolean {
    return this.variant() === 'interactive';
  }

  private isFromNestedControl(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    const currentTarget = event.currentTarget as HTMLElement | null;
    if (!target || !currentTarget || target === currentTarget) {
      return false;
    }

    const control = target.closest('button, a, input, textarea, select, [role="button"], [role="link"]');
    return !!control && control !== currentTarget && currentTarget.contains(control);
  }
}
